import { FunctionItem3D } from '../types/math';

export const functions3D: FunctionItem3D[] = [
  {
    id: 'elliptic_paraboloid',
    dimension: '3d',
    title: 'Elliptic Paraboloid (The Bowl)',
    category: 'quadric',
    type: 'explicit',
    formulaLatex: 'z = x^2 + y^2',
    dynamicFormulaLatex: (p) => {
      const a = p.a?.toFixed(1) ?? '1.0';
      const b = p.b?.toFixed(1) ?? '1.0';
      return `z = ${a}x^2 + ${b}y^2`;
    },
    evaluateExplicit: (x, y, p) => {
      const a = p?.a ?? 1;
      const b = p?.b ?? 1;
      return a * x * x + b * y * y;
    },
    uDomain: [-3, 3],
    vDomain: [-3, 3],
    zRange: [0, 18],
    intuition: {
      summary: 'Rotational bowl opening upward with circular/elliptical horizontal cross-sections.',
      keySignatures: [
        'Global minimum apex at (0, 0, 0)',
        'Traces parallel to the xy-plane (z = c > 0) are circles: x² + y² = c',
        'Vertical slices (x = 0 or y = 0) are standard upward-opening parabolas'
      ],
      crossSections: [
        { plane: 'z = constant (c > 0)', description: 'Concentric circles x² + y² = c', formulaLatex: 'x^2 + y^2 = c' },
        { plane: 'y = 0 (xz-plane)', description: 'Upward parabola', formulaLatex: 'z = x^2' },
        { plane: 'x = 0 (yz-plane)', description: 'Upward parabola', formulaLatex: 'z = y^2' }
      ]
    },
    distractors: [
      { formulaLatex: 'z = x^2 - y^2', explanation: 'A minus sign creates a saddle with both upward and downward parabolas.' },
      { formulaLatex: 'z = -(x^2 + y^2)', explanation: 'Negative paraboloid opens downward like an umbrella or dome.' },
      { formulaLatex: 'z = \\sqrt{x^2 + y^2}', explanation: 'Square root produces a sharp cone with straight diagonal sides, not curved parabolic walls.' }
    ],
    parameters: {
      a: { min: 0.2, max: 2, default: 1, step: 0.2, label: 'X Curvature (a)' },
      b: { min: 0.2, max: 2, default: 1, step: 0.2, label: 'Y Curvature (b)' }
    }
  },
  {
    id: 'hyperbolic_paraboloid',
    dimension: '3d',
    title: 'Hyperbolic Paraboloid (The Saddle)',
    category: 'quadric',
    type: 'explicit',
    formulaLatex: 'z = x^2 - y^2',
    dynamicFormulaLatex: (p) => {
      const a = p.a?.toFixed(1) ?? '1.0';
      const b = p.b?.toFixed(1) ?? '1.0';
      return `z = ${a}x^2 - ${b}y^2`;
    },
    evaluateExplicit: (x, y, p) => {
      const a = p?.a ?? 1;
      const b = p?.b ?? 1;
      return a * x * x - b * y * y;
    },
    uDomain: [-3, 3],
    vDomain: [-3, 3],
    zRange: [-9, 9],
    intuition: {
      summary: 'Iconic mountain pass / Pringle chip with a stationary saddle point at the origin.',
      keySignatures: [
        'Saddle point at (0, 0, 0): simultaneously a minimum along the x-axis and a maximum along the y-axis',
        'Horizontal cross-sections (z = c) are hyperbolas that rotate 90° across z = 0',
        'Ruled surface: constructed entirely from straight lines even though it looks curved!'
      ],
      crossSections: [
        { plane: 'y = 0 (xz-plane)', description: 'Parabola opening UPWARD', formulaLatex: 'z = x^2' },
        { plane: 'x = 0 (yz-plane)', description: 'Parabola opening DOWNWARD', formulaLatex: 'z = -y^2' },
        { plane: 'z = constant', description: 'Hyperbolas x² - y² = c', formulaLatex: 'x^2 - y^2 = c' }
      ]
    },
    distractors: [
      { formulaLatex: 'z = x^2 + y^2', explanation: 'Plus sign makes all directions curve upward, producing a bowl.' },
      { formulaLatex: 'z = y^2 - x^2', explanation: 'Swapping terms rotates the saddle 90°: curving up along y and down along x.' },
      { formulaLatex: 'z = xy', explanation: 'z = xy is the same saddle geometry but rotated 45° around the z-axis.' }
    ],
    parameters: {
      a: { min: 0.5, max: 2, default: 1, step: 0.25, label: 'Upward Curvature (a)' },
      b: { min: 0.5, max: 2, default: 1, step: 0.25, label: 'Downward Curvature (b)' }
    }
  },
  {
    id: 'inverted_paraboloid',
    dimension: '3d',
    title: 'Inverted Paraboloid (The Dome)',
    category: 'quadric',
    type: 'explicit',
    formulaLatex: 'z = 4 - (x^2 + y^2)',
    evaluateExplicit: (x, y) => 4 - (x * x + y * y),
    uDomain: [-3, 3],
    vDomain: [-3, 3],
    zRange: [-14, 5],
    intuition: {
      summary: 'Downward-opening paraboloid forming a hill or domed ceiling.',
      keySignatures: [
        'Global maximum peak at (0, 0, 4)',
        'Plunges to negative values as distance r = √(x²+y²) increases beyond 2',
        'All vertical slices are inverted parabolas opening downward'
      ],
      crossSections: [
        { plane: 'z = 0 (xy-plane)', description: 'Circle of radius 2: x² + y² = 4', formulaLatex: 'x^2 + y^2 = 4' },
        { plane: 'y = 0 (xz-plane)', description: 'Downward parabola with peak at (0, 4)', formulaLatex: 'z = 4 - x^2' }
      ]
    },
    distractors: [
      { formulaLatex: 'z = x^2 + y^2 - 4', explanation: 'This is an upright bowl with its bottom at z = -4.' },
      { formulaLatex: 'z = 4 - \\sqrt{x^2 + y^2}', explanation: 'A cone has straight slanted sides meeting at a sharp peak, not a rounded parabolic crest.' },
      { formulaLatex: 'z = 4 - (x^2 - y^2)', explanation: 'Contains a minus sign inside, which forms an inverted saddle rather than a dome.' }
    ]
  },
  {
    id: 'circular_cone',
    dimension: '3d',
    title: 'Circular Cone',
    category: 'quadric',
    type: 'explicit',
    formulaLatex: 'z = \\sqrt{x^2 + y^2}',
    evaluateExplicit: (x, y) => Math.sqrt(x * x + y * y),
    uDomain: [-4, 4],
    vDomain: [-4, 4],
    zRange: [0, 6],
    intuition: {
      summary: 'Classic right circular cone with straight conical walls meeting at a sharp singular point.',
      keySignatures: [
        'Pointed singular vertex at (0, 0, 0) with undefined gradient at the apex',
        'Linear slope: z is proportional to the radius r = √(x² + y²)',
        'Cross-sections along radial lines from origin are straight lines z = r'
      ],
      crossSections: [
        { plane: 'y = 0 (xz-plane)', description: 'V-shape absolute value: z = |x|', formulaLatex: 'z = |x|' },
        { plane: 'z = constant (c > 0)', description: 'Circles x² + y² = c²', formulaLatex: 'x^2 + y^2 = c^2' }
      ]
    },
    distractors: [
      { formulaLatex: 'z = x^2 + y^2', explanation: 'A paraboloid has curved, accelerating walls rather than straight triangular cross-sections.' },
      { formulaLatex: 'z = -\\sqrt{x^2 + y^2}', explanation: 'Inverted cone hanging upside down pointing into negative z.' },
      { formulaLatex: 'z = \\sqrt{x^2 - y^2}', explanation: 'Only defined when |x| ≥ |y|, which produces a wedge, not a full 360° circular cone.' }
    ]
  },
  {
    id: 'monkey_saddle',
    dimension: '3d',
    title: 'The Monkey Saddle',
    category: 'waves_extrema',
    type: 'explicit',
    formulaLatex: 'z = x^3 - 3xy^2',
    dynamicFormulaLatex: (p) => {
      const s = p.s?.toFixed(1) ?? '1.0';
      return `z = \\frac{x^3 - 3xy^2}{${s}}`;
    },
    evaluateExplicit: (x, y, p) => {
      const s = p?.s ?? 1;
      return (x * x * x - 3 * x * y * y) / s;
    },
    uDomain: [-2.5, 2.5],
    vDomain: [-2.5, 2.5],
    zRange: [-16, 16],
    intuition: {
      summary: 'A saddle surface designed with three downward dips: two for a monkey\'s legs and one for its tail!',
      keySignatures: [
        'Three-fold rotational symmetry (120° angles between the 3 hills and 3 valleys)',
        'Origin (0,0,0) is an inflection point with third-order vanishing derivatives',
        'Real part of the complex cubic function: z = Re((x + iy)³)'
      ],
      crossSections: [
        { plane: 'y = 0', description: 'Pure cubic curve z = x³', formulaLatex: 'z = x^3' },
        { plane: 'z = 0', description: 'Three intersecting straight lines at 60° intervals: x(x - √3 y)(x + √3 y) = 0', formulaLatex: 'x^3 - 3xy^2 = 0' }
      ]
    },
    distractors: [
      { formulaLatex: 'z = x^2 - y^2', explanation: 'The standard hyperbolic saddle has only 2 dips and 2 rises (quadric).' },
      { formulaLatex: 'z = x^3 + y^3', explanation: 'Lacks the 3-fold radial symmetry and has only a single diagonal saddle ridge.' },
      { formulaLatex: 'z = x^4 - y^4', explanation: 'Degree 4 surface with 4-fold symmetry, not 3-fold.' }
    ],
    parameters: {
      s: { min: 0.5, max: 2, default: 1, step: 0.25, label: 'Scale Factor (s)' }
    }
  },
  {
    id: 'sinc_ripple_3d',
    dimension: '3d',
    title: 'Sombrero / Sinc Ripple',
    category: 'waves_extrema',
    type: 'explicit',
    formulaLatex: 'z = \\frac{\\sin(\\sqrt{x^2+y^2})}{\\sqrt{x^2+y^2}}',
    evaluateExplicit: (x, y) => {
      const r = Math.sqrt(x * x + y * y);
      if (r < 0.0001) return 1;
      return Math.sin(r) / r;
    },
    uDomain: [-12, 12],
    vDomain: [-12, 12],
    zRange: [-0.4, 1.2],
    intuition: {
      summary: 'Concentric circular waves rippling outward from a central crest like a droplet in water.',
      keySignatures: [
        'Rotational invariance: depends purely on the radius r = √(x² + y²)',
        'Central maximum at r = 0 with peak height z = 1',
        'Concentric circular troughs and crests whose amplitude dies out as 1/r'
      ],
      crossSections: [
        { plane: 'Radial slice through origin', description: 'Standard 2D sinc wave z = sin(r)/r', formulaLatex: 'z = \\frac{\\sin(r)}{r}' },
        { plane: 'z = constant', description: 'Concentric circles', formulaLatex: 'x^2 + y^2 = r_k^2' }
      ]
    },
    distractors: [
      { formulaLatex: 'z = \\cos(\\sqrt{x^2+y^2})', explanation: 'Pure cosine never decays; its wave crests stay at constant height 1 indefinitely.' },
      { formulaLatex: 'z = e^{-(x^2+y^2)}', explanation: 'A Gaussian is a smooth single hill with no rippling wave oscillations.' },
      { formulaLatex: 'z = \\sin(x)\\sin(y)', explanation: 'Produces a rectangular egg-carton grid, not concentric circular ripples.' }
    ]
  },
  {
    id: 'gaussian_hill_3d',
    dimension: '3d',
    title: '2D Gaussian Mountain',
    category: 'waves_extrema',
    type: 'explicit',
    formulaLatex: 'z = 3e^{-(x^2+y^2)}',
    evaluateExplicit: (x, y) => 3 * Math.exp(-(x * x + y * y) / 2),
    uDomain: [-4, 4],
    vDomain: [-4, 4],
    zRange: [-0.2, 3.2],
    intuition: {
      summary: 'Smooth, isolated symmetrical peak decaying exponentially in all directions.',
      keySignatures: [
        'Rotational symmetry with peak of height 3 at the origin',
        'Tail values drop super-exponentially toward 0 in every direction',
        'Foundational model for 2D normal distribution and laser beam profiles'
      ],
      crossSections: [
        { plane: 'y = 0 (xz-plane)', description: '1D Gaussian bell curve z = 3e^(-x²/2)', formulaLatex: 'z = 3e^{-x^2/2}' },
        { plane: 'z = constant', description: 'Circles x² + y² = -2 ln(z/3)', formulaLatex: 'x^2 + y^2 = c' }
      ]
    },
    distractors: [
      { formulaLatex: 'z = \\frac{3}{1 + x^2 + y^2}', explanation: 'The 2D Cauchy surface has much flatter, wider algebraic tails.' },
      { formulaLatex: 'z = 3 - x^2 - y^2', explanation: 'A paraboloid plunges indefinitely into negative infinity rather than flattening at z = 0.' },
      { formulaLatex: 'z = 3e^{-(|x|+|y|)}', explanation: 'Has diamond-shaped contour ridges with sharp corners along the axes.' }
    ]
  },
  {
    id: 'egg_carton',
    dimension: '3d',
    title: 'Egg Carton Landscape',
    category: 'waves_extrema',
    type: 'explicit',
    formulaLatex: 'z = \\sin(x)\\cos(y)',
    evaluateExplicit: (x, y) => Math.sin(x) * Math.cos(y),
    uDomain: [-6, 6],
    vDomain: [-6, 6],
    zRange: [-1.2, 1.2],
    intuition: {
      summary: 'Periodic checkerboard pattern of alternating hills, valleys, and saddle passes.',
      keySignatures: [
        'Grid of local maxima (height +1) and minima (height -1)',
        'Saddle points connect adjacent hills and valleys',
        'Periodic in both x and y with period 2π'
      ],
      crossSections: [
        { plane: 'x = π/2', description: 'Cosine wave: z = cos(y)', formulaLatex: 'z = \\cos(y)' },
        { plane: 'y = 0', description: 'Sine wave: z = sin(x)', formulaLatex: 'z = \\sin(x)' }
      ]
    },
    distractors: [
      { formulaLatex: 'z = \\sin(x) + \\cos(y)', explanation: 'Sum of waves creates diagonal ridges rather than an isolated grid of egg cups.' },
      { formulaLatex: 'z = \\sin(x^2 + y^2)', explanation: 'Creates circular ripples that become infinitely compressed as radius grows.' },
      { formulaLatex: 'z = \\cos(xy)', explanation: 'Creates hyperbolic contour patterns rather than a uniform rectangular grid.' }
    ]
  },
  {
    id: 'torus_3d',
    dimension: '3d',
    title: 'Parametric Torus (Doughnut)',
    category: 'parametric_topological',
    type: 'parametric',
    formulaLatex: '\\begin{cases} x = (R + r\\cos v)\\cos u \\\\ y = (R + r\\cos v)\\sin u \\\\ z = r\\sin v \\end{cases}',
    evaluateParametric: (u, v) => {
      const R = 3; // Major radius
      const r = 1.2; // Minor radius
      const x = (R + r * Math.cos(v)) * Math.cos(u);
      const y = (R + r * Math.cos(v)) * Math.sin(u);
      const z = r * Math.sin(v);
      return [x, y, z];
    },
    uDomain: [0, 2 * Math.PI],
    vDomain: [0, 2 * Math.PI],
    zRange: [-1.5, 1.5],
    intuition: {
      summary: 'Closed doughnut-shaped surface of genus 1 formed by sweeping a circle around an axis.',
      keySignatures: [
        'Central circular hole (topological genus 1)',
        'u is the toroidal angle (around the big circle, 0 to 2π)',
        'v is the poloidal angle (around the tube cross-section, 0 to 2π)'
      ],
      crossSections: [
        { plane: 'Vertical cut through center', description: 'Two disjoint circles of radius r', formulaLatex: '(x \\pm R)^2 + z^2 = r^2' },
        { plane: 'Horizontal midplane (z = 0)', description: 'Two concentric circles of radii R - r and R + r', formulaLatex: 'x^2 + y^2 = (R \\pm r)^2' }
      ]
    },
    distractors: [
      { formulaLatex: 'x^2 + y^2 + z^2 = R^2', explanation: 'This is a standard 2-sphere with no central hole.' },
      { formulaLatex: '\\begin{cases} x = r\\sin u\\cos v \\\\ y = r\\sin u\\sin v \\\\ z = r\\cos u \\end{cases}', explanation: 'This is the standard spherical parametrization for a closed ball.' },
      { formulaLatex: 'z = x^2 + y^2', explanation: 'An elliptic paraboloid is an open bowl, not a closed ring with a hole.' }
    ]
  },
  {
    id: 'mobius_strip',
    dimension: '3d',
    title: 'Möbius Strip',
    category: 'parametric_topological',
    type: 'parametric',
    formulaLatex: '\\begin{cases} x = \\left(1 + \\frac{v}{2}\\cos\\frac{u}{2}\\right)\\cos u \\\\ y = \\left(1 + \\frac{v}{2}\\cos\\frac{u}{2}\\right)\\sin u \\\\ z = \\frac{v}{2}\\sin\\frac{u}{2} \\end{cases}',
    evaluateParametric: (u, v) => {
      const radius = 3;
      const w = v * 0.8;
      const x = (radius + w * Math.cos(u / 2)) * Math.cos(u);
      const y = (radius + w * Math.cos(u / 2)) * Math.sin(u);
      const z = w * Math.sin(u / 2);
      return [x, y, z];
    },
    uDomain: [0, 2 * Math.PI],
    vDomain: [-1, 1],
    zRange: [-1.2, 1.2],
    intuition: {
      summary: 'Famous non-orientable topological ribbon with only ONE side and ONE continuous boundary edge.',
      keySignatures: [
        'The ribbon performs a half-twist (u/2) while completing one full 360° circle (u)',
        'Non-orientable: traversing the loop once brings you back upside down!',
        'Boundary is a single closed curve homeomorphic to a circle'
      ],
      crossSections: [
        { plane: 'Radial slice at fixed u', description: 'Straight line segment tilting at angle u/2', formulaLatex: '\\text{Line tilt} = u/2' }
      ]
    },
    distractors: [
      { formulaLatex: '\\begin{cases} x = R\\cos u \\\\ y = R\\sin u \\\\ z = v \\end{cases}', explanation: 'Standard straight cylinder with 2 distinct sides (inside/outside) and 2 boundary rims.' },
      { formulaLatex: '\\begin{cases} x = (R + v)\\cos u \\\\ y = (R + v)\\sin u \\\\ z = 0 \\end{cases}', explanation: 'Flat planar annulus (washer disk) with no half-twist.' },
      { formulaLatex: 'z = x^2 - y^2', explanation: 'A saddle is a quadric surface defined over a square plane, not a twisted looped ribbon.' }
    ]
  },
  {
    id: 'helicoid',
    dimension: '3d',
    title: 'Helicoid (Spiral Ramp)',
    category: 'parametric_topological',
    type: 'parametric',
    formulaLatex: '\\begin{cases} x = u\\cos v \\\\ y = u\\sin v \\\\ z = c v \\end{cases}',
    evaluateParametric: (u, v) => {
      const c = 0.5;
      const x = u * Math.cos(v);
      const y = u * Math.sin(v);
      const z = c * v;
      return [x, y, z];
    },
    uDomain: [-2.5, 2.5],
    vDomain: [-Math.PI, Math.PI],
    zRange: [-2, 2],
    intuition: {
      summary: 'Spiral staircase minimal surface whose mean curvature is zero everywhere.',
      keySignatures: [
        'Only ruled minimal surface other than the plane',
        'Rotates as it rises: vertical position z is directly proportional to angle v',
        'Locally isometric to the catenoid!'
      ],
      crossSections: [
        { plane: 'z = constant', description: 'Straight lines rotating around the z-axis', formulaLatex: 'y = x\\tan(z/c)' },
        { plane: 'r = constant cylinder', description: 'Standard circular helix', formulaLatex: 'z = c\\theta' }
      ]
    },
    distractors: [
      { formulaLatex: 'z = \\arctan(y / x)', explanation: 'Has a branch-cut discontinuity unless treated continuously like the parametric helicoid.' },
      { formulaLatex: 'z = \\sqrt{x^2 + y^2}', explanation: 'A cone radiates outwards symmetrically without any spiral rotation along z.' },
      { formulaLatex: '\\begin{cases} x = r\\cos v \\\\ y = r\\sin v \\\\ z = c v \\end{cases}', explanation: 'This is a 1D helical curve (wire spring), not a 2D surface ramp.' }
    ]
  }
];
